import { Injectable } from '@angular/core';
import { InvestisseurPatrimoine } from 'projects/minotaure-lib/src/public-api';

@Injectable({ providedIn: 'root' })
export class PatrimoineCalculatorService {
  public computeFiscalValues(valeurBien: number, invs: InvestisseurPatrimoine[]): Map<number, number> {
    const result = new Map<number, number>();

    if (!valeurBien || !invs?.length) return result;

    for (const inv of invs) {
      result.set(inv.id ?? 0, 0);
    }

    // Totaux
    const npHolders = invs.filter(x => (x.detentionNP ?? 0) > 0);
    const totalNP = npHolders.reduce((sum, x) => sum + (x.detentionNP ?? 0), 0) / 100;
    const totalUS = invs.reduce((sum, x) => sum + (x.detentionUS ?? 0), 0) / 100;

    // Pleine propriété
    for (const inv of invs) {
      const ppPct = (inv.detentionPP ?? 0) / 100;
      if (ppPct > 0) {
        result.set(inv.id, result.get(inv.id)! + valeurBien * ppPct);
      }
    }

    // Usufruit
    for (const usufruitier of invs) {
      const usPct = (usufruitier.detentionUS ?? 0) / 100;
      if (usPct <= 0) continue;

      const dateNaissance =
        usufruitier.investisseurId > 0 ? usufruitier.investisseur?.personnePhysique?.[0]?.dateNaissance : usufruitier.dateNaissance;
      if (!dateNaissance) continue;

      const age = this.calculateAge(dateNaissance);
      if (!age) continue;

      const rate = this.getUsufruitRate(age);

      const baseValue = valeurBien * usPct;

      // Valeur fiscale de l’usufruit
      const usValue = baseValue * rate;
      result.set(usufruitier.id, result.get(usufruitier.id)! + usValue);

      // NP générée
      const npValue = baseValue * (1 - rate);

      if (npValue <= 0 || totalNP <= 0 || totalUS <= 0) continue;

      // Plafonnement à la masse NP réelle
      const npValueCapped = npValue * (totalNP / totalUS);

      // Répartition proportionnelle entre NP
      for (const np of npHolders) {
        const npPct = (np.detentionNP ?? 0) / 100;
        const share = npPct / totalNP;

        result.set(np.id, result.get(np.id)! + npValueCapped * share);
      }
    }

    return result;
  }

  private getUsufruitRate(age: number): number {
    if (age == null) return 0;
    if (age < 21) return 0.9;
    if (age <= 30) return 0.8;
    if (age <= 40) return 0.7;
    if (age <= 50) return 0.6;
    if (age <= 60) return 0.5;
    if (age <= 70) return 0.4;
    if (age <= 80) return 0.3;
    if (age <= 90) return 0.2;
    return 0.1;
  }

  private calculateAge(birthDate: Date | string): number {
    const birth = new Date(birthDate);
    const ref = new Date();

    let age = ref.getFullYear() - birth.getFullYear();

    const hasHadBirthdayThisYear =
      ref.getMonth() > birth.getMonth() || (ref.getMonth() === birth.getMonth() && ref.getDate() >= birth.getDate());

    if (!hasHadBirthdayThisYear) {
      age--;
    }

    return age;
  }
}
